import { v4 as uuidv4 } from "uuid";

export interface ILabeledField<K extends number | string> {
  value: K;
  label: string | null;
}

// Distributes over a union, so a `SingleOrArray<ILabeledField<K>>` field maps to
// `K | K[]` instead of falling through unmapped.
type RawValue<V> =
  V extends ILabeledField<infer K2> ? K2 :
  V extends Array<ILabeledField<infer K2>> ? K2[] : V;

export type RawFilters<T> = {
  [K in keyof T]?: RawValue<T[K]>;
}

// Query-string params may arrive list-style (`community[]=1&community[]=2`).
const LIST_KEY_SUFFIX = "[]";

// Flatten a native value into its comma-separated tokens, which is the shape the
// per-search models already expect for multi-value filters (see `splitIntoArray`).
function toTokens(value: unknown): string[] {
  return (Array.isArray(value) ? value : [value])
    .filter(x => x !== undefined && x !== null)
    .flatMap(x => String(x).split(","))
    .map(x => x.trim())
    .filter(Boolean);
}

export enum eSearchFilterType {
  Search = "search",
  Filter = "filter",
  NextPage = "next_page",
}

export interface IFiltersPage
{
  start: number;
  limit: number;
  page: number;
}

export function toLabeledField<K extends number | string, K2 extends number | string>(input: Array<K | ILabeledField<K>>, mapper: (value: K) => K2): ILabeledField<K2>[];
export function toLabeledField<K extends number | string, K2 extends number | string>(input: K | ILabeledField<K>, mapper: (value: K) => K2): ILabeledField<K2>;
export function toLabeledField<K extends number | string>(input: Array<K| ILabeledField<K>>): ILabeledField<K>[];
export function toLabeledField<K extends number | string>(input: K | ILabeledField<K>): ILabeledField<K>;
export function toLabeledField<K extends number | string, K2 extends number | string>(input: K | K[] | ILabeledField<K> | ILabeledField<K>[], mapper?: (value: K) => K2)
  : ILabeledField<K2 | K> | ILabeledField<K2 | K>[]
{
  if(input === undefined || input === null) { return input; }

  // Handling arrays
  if(Array.isArray(input)) {
    return input.map(x => toLabeledField(x, mapper as any));
  }

  const hasLabel = typeof input === "object" && "value" in input;
  if(hasLabel && !mapper) { return input; }

  const value: K = hasLabel? input.value: input;
  return { value: mapper?.(value) ?? value, label: null };
}

export abstract class SearchFilterOperationModel<HarFilters, Filters, ToolInput>
{
  public abstract DEFAULT_LIMIT: number;
  public readonly id = uuidv4();

  
  public abstract fromFiltersToToolInput(filters: Filters | RawFilters<Filters>): ToolInput;
  public abstract getSearchUserMessage(filters: ToolInput, type: eSearchFilterType): string;
  public abstract getNextPageUserMessage(filters: ToolInput): string;
  protected abstract convertNativeToFilters(filters: HarFilters): RawFilters<Filters>;
  protected abstract applyNextPage(filters: ToolInput): ToolInput;

  public fromNativeToFilters(filters: HarFilters): RawFilters<Filters>
  {
    return this.convertNativeToFilters(this.normalizeNativeFilters(filters));
  }

  /**
   * Native filters come from the URL query string, where multi-value params may
   * be sent with a `[]` suffix (`zip_code[]=77027&zip_code[]=77098`). Strip the
   * suffix so the key matches what `convertNativeToFilters` looks for, and fold
   * the value(s) into the comma-separated form the models parse. When both the
   * plain and the `[]` key are present, their values are merged (de-duplicated).
   */
  protected normalizeNativeFilters(filters: HarFilters): HarFilters
  {
    if (!filters || typeof filters !== "object") { return filters; }

    const normalized: Record<string, unknown> = {};
    for (const [rawKey, value] of Object.entries(filters)) {
      const isList = rawKey.endsWith(LIST_KEY_SUFFIX);
      const key = isList ? rawKey.slice(0, -LIST_KEY_SUFFIX.length) : rawKey;

      // Scalars on a plain key are passed through untouched; only list-style
      // values (or a key seen twice) are collapsed into a comma-separated string.
      if (!isList && !Array.isArray(value) && !(key in normalized)) {
        normalized[key] = value;
        continue;
      }

      const tokens = [...toTokens(normalized[key]), ...toTokens(value)];
      normalized[key] = Array.from(new Set(tokens)).join(",");
    }

    return normalized as HarFilters;
  }

  public fromNativeToToolInput(native: HarFilters): ToolInput
  {
    const filters = this.fromNativeToFilters(native);
    return this.fromFiltersToToolInput(filters);
  }

  public normalizeFilters(filters: ToolInput, type: eSearchFilterType): ToolInput
  {
    if(type === eSearchFilterType.NextPage) {
      return this.applyNextPage(filters);
    }
    // New search or filter of existing search: reset pagination to first page
    return { ...filters, start: 0 };
  }

  protected getPage(start: number | undefined, limit: number | undefined): IFiltersPage
  {
    start = start || 0;
    limit = limit || this.DEFAULT_LIMIT;
    const page = Math.floor(start / limit) + 1;
    return { start, limit, page };
  }

  protected getNextPage(start: number | undefined, limit: number | undefined): IFiltersPage {
    const paging = this.getPage(start, limit);
    paging.start = paging.page * paging.limit;
    paging.page += 1;
    return paging;
  }

  public toRaw<T>(filters: T | RawFilters<T>): RawFilters<T>;
  public toRaw<T>(filters: T): RawFilters<T>
  {
    const rawFilters: RawFilters<T> = {} as RawFilters<T>;
    for (const key in filters) {
      const value = filters[key];
      if (value === undefined || value === null) { continue; }
      if (typeof value === "object" && "value" in value) {
        rawFilters[key] = value.value as any;
      } else if (Array.isArray(value) && value.every(v => typeof v === "object" && "value" in v)) {
        rawFilters[key] = value.map(v => v.value) as any;
      } else {
        rawFilters[key] = value as any;
      }
    }

    return rawFilters;
  }

  protected createComponentMessage(
    tag: string,
    //filters: ToolInput | RawFilters<ToolInput>,
    pretext?: string,
  ) {
    // const attributes = Object.entries(filters)
    //   .filter(([key, value]) => !!value)
    //   .flatMap(([key, value]) => `${key}="${value}"`)
    //   .join(" ");
    //filters = this.toRaw(filters);
    //const filterValue = JSON.stringify(JSON.stringify(filters));
    const component = `<${tag} search_id='${this.id}'></${tag}>`;
    return pretext ? `${pretext}: ${component}` : component;
  }
}
